import {Check, ExternalLink, Scale, Star, TrendingUp, X, Zap} from 'lucide-react'
import { Link } from "react-router-dom"
import Breadcrumbs from "@/components/seo/Breadcrumbs"
import JsonLd from "@/components/seo/JsonLd"
import MedicalDisclaimer from "@/components/seo/MedicalDisclaimer"
import SeoHead, { SITE_NAME, SITE_URL } from "@/components/seo/SeoHead"
import { getReviewBySlug } from "@/data/reviews"
import type { ComparisonGuide } from "@/data/comparisons"

interface ComparisonPageTemplateProps {
  guide: ComparisonGuide
}

function FeatureValue({ value }: { value: string | boolean }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="w-5 h-5 text-green-600" />
    ) : (
      <X className="w-5 h-5 text-red-500" />
    )
  }

  return <span className="font-semibold">{value}</span>
}

function ComparisonPageTemplate({ guide }: ComparisonPageTemplateProps) {
  const path = `/comparison/${guide.slug}`
  const url = `${SITE_URL}${path}`
  const relatedReviews = guide.relatedReviewSlugs
    .map((slug) => getReviewBySlug(slug))
    .filter((item): item is NonNullable<typeof item> => Boolean(item))

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Comparison Guides", item: `${SITE_URL}/comparison` },
        { "@type": "ListItem", position: 3, name: guide.title, item: url }
      ]
    },
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: guide.title,
      description: guide.seoDescription,
      author: {
        "@type": "Organization",
        name: SITE_NAME
      },
      publisher: {
        "@type": "Organization",
        name: SITE_NAME
      },
      mainEntityOfPage: url
    }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-teal-50">
      <SeoHead title={guide.seoTitle} description={guide.seoDescription} path={path} />
      <JsonLd data={schema} />

      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
              <Scale className="w-5 h-5" />
              <span className="text-sm font-medium">Head-to-Head Comparison</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">{guide.title}</h1>
            <p className="text-xl text-teal-100 max-w-3xl mx-auto">{guide.subtitle}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Breadcrumbs
          items={[
            { label: "Home", href: "/" },
            { label: "Comparison Guides", href: "/comparison" },
            { label: guide.title }
          ]}
        />

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {guide.products.map((product) => (
            <div key={product.name} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow">
              <div className="bg-gradient-to-br from-teal-50 to-orange-50 p-8">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-48 h-48 object-cover rounded-xl mx-auto mb-4"
                />
              </div>
              <div className="p-8">
                <div className="text-sm text-gray-500 mb-1">{product.brand}</div>
                <h2 className="text-2xl font-bold text-gray-900 mb-3">{product.name}</h2>

                <div className="flex items-center gap-2 mb-4">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.floor(product.rating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="font-semibold text-gray-900">{product.rating}</span>
                </div>

                <div className="text-3xl font-bold text-teal-600 mb-4">{product.price}</div>

                <div className="bg-teal-50 rounded-lg p-4 mb-6">
                  <div className="text-sm font-medium text-gray-700 mb-1">Best For:</div>
                  <div className="text-gray-900 font-semibold">{product.overall}</div>
                </div>

                {product.reviewSlug ? (
                  <Link
                    to={`/reviews/${product.reviewSlug}`}
                    className="block text-center text-teal-700 font-medium mb-4 hover:text-teal-800"
                  >
                    Read full {product.name} review
                  </Link>
                ) : null}

                <a
                  href={product.affiliateUrl}
                  className="block w-full text-center bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-semibold transition-colors"
                >
                  Check Best Price
                  <ExternalLink className="w-4 h-4 inline ml-2" />
                </a>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-16">
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-6">
            <h2 className="text-2xl font-bold">Detailed Feature Comparison</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Feature</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{guide.leftLabel}</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">{guide.rightLabel}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {guide.features.map((row) => (
                  <tr key={row.feature} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{row.feature}</td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <FeatureValue value={row.left} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      <FeatureValue value={row.right} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {guide.products.map((product) => (
            <div key={`${product.name}-pros`} className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-6">{product.name}</h3>

              <div className="mb-6">
                <h4 className="text-lg font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  Pros
                </h4>
                <ul className="space-y-2">
                  {product.pros.map((pro) => (
                    <li key={pro} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-green-600 mt-2 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{pro}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-red-700 mb-3 flex items-center gap-2">
                  <X className="w-5 h-5" />
                  Cons
                </h4>
                <ul className="space-y-2">
                  {product.cons.map((con) => (
                    <li key={con} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-red-600 mt-2 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{con}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-2xl p-8 mb-16">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6 text-teal-600" />
            Which One Should You Choose?
          </h2>
          <div className="space-y-4">
            {guide.useCases.map((useCase) => (
              <div key={useCase.scenario} className="bg-white rounded-lg p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{useCase.scenario}</h3>
                    <p className="text-sm text-gray-600">{useCase.reason}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full font-semibold text-sm">
                      <TrendingUp className="w-4 h-4" />
                      Winner: {useCase.winner}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Final Verdict</h2>
          <div className="space-y-4">
            {guide.verdict.map((paragraph) => (
              <p key={paragraph.slice(0, 24)} className="text-gray-700 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </div>

        {relatedReviews.length > 0 ? (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Related Reviews</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedReviews.map((review) => (
                <Link
                  key={review.slug}
                  to={`/reviews/${review.slug}`}
                  className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow"
                >
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{review.name} Review</h3>
                  <p className="text-sm text-gray-600">{review.tagline}</p>
                </Link>
              ))}
            </div>
          </section>
        ) : null}

        <MedicalDisclaimer extra="Vitamin Vital Portal may earn affiliate commissions from purchases made through links on this page." />
      </div>
    </div>
  )
}

export default ComparisonPageTemplate
